import { IconAlertCircle } from '@tabler/icons-react-native'
import {
  CameraType,
  getCameraPermissionsAsync,
  getMediaLibraryPermissionsAsync,
  launchCameraAsync,
  launchImageLibraryAsync,
  requestCameraPermissionsAsync,
  requestMediaLibraryPermissionsAsync,
  type ImagePickerAsset
} from 'expo-image-picker'
import * as Linking from 'expo-linking'
import { useCallback, useState, type FC, type ReactNode } from 'react'

import { Dialog, HStack, Icon, Toast, ToastDescription, ToastTitle, useToast } from '@/components'
import { MediaBuckets, MediaContext } from '@/contexts'
import { useAuth, useDisclosure } from '@/hooks'
import { COLOR_BLACK, normalizeImage, supabase } from '@/lib'

export interface MediaProviderProps {
  children: ReactNode
}

export const MediaProvider: FC<MediaProviderProps> = ({ children }) => {
  const { session } = useAuth()
  const toast = useToast()
  const [showPermissionsDialog, permissionsDialog] = useDisclosure(false)
  const [isUploadingMedia, setIsUploadingMedia] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<ImagePickerAsset | null>(null)

  const showMediaError = useCallback(
    () =>
      toast.show({
        placement: 'bottom',
        render: () => (
          <Toast
            action='warning'
            variant='solid'
          >
            <HStack className='items-start gap-2'>
              <Icon
                as={IconAlertCircle}
                stroke={COLOR_BLACK}
                size='xl'
              />
              <ToastTitle>Photo Not Added</ToastTitle>
            </HStack>
            <ToastDescription>Something went wrong adding that photo. Please try again.</ToastDescription>
          </Toast>
        )
      }),
    [toast]
  )

  const uploadMedia = useCallback(
    async (asset: ImagePickerAsset, bucket: MediaBuckets = MediaBuckets.Profile) => {
      setIsUploadingMedia(true)

      try {
        const imageData = await normalizeImage(asset)
        const { data, error } = await supabase.storage
          .from(bucket)
          .upload(`${session?.user?.id}/${Date.now()}`, imageData, {
            cacheControl: '31556926', // one year
            contentType: 'image/webp',
            upsert: true
          })

        if (error) {
          throw error
        }

        return data
      } catch (error) {
        // callers all guard on the returned id, so surface the failure here rather than silently returning nothing
        console.warn('[media] upload failed', error)
        showMediaError()

        return null
      } finally {
        // always clear the flag, otherwise a failed upload leaves every media button spinning and disabled forever
        setIsUploadingMedia(false)
      }
    },
    [session?.user?.id, showMediaError]
  )
  const getCameraMedia = useCallback(async () => {
    // check to see if we already have permission. this doesn't trigger the request dialog, it only checks for
    // existing permissions
    const { status: existingStatus } = await getCameraPermissionsAsync()

    let finalStatus = existingStatus

    // if they have previously denied access in the past, we can only prompt the user to re-enable permissions in the
    // OS settings, we can't do anything else until they do
    if (existingStatus === 'denied') {
      permissionsDialog.open()
    } else if (existingStatus === 'undetermined') {
      // if we've never asked before, let's do so now!
      const { status } = await requestCameraPermissionsAsync()

      if (status === 'denied') {
        permissionsDialog.open()
      }

      finalStatus = status
    }

    // either we already had it, or they just allowed it, but we have permissions now
    if (finalStatus === 'granted') {
      setSelectedAsset(null)

      try {
        const result = await launchCameraAsync({
          allowsEditing: true,
          aspect: [4, 4], // 1:1
          cameraType: CameraType.front,
          exif: false,
          mediaTypes: ['images'],
          quality: 1 // optimize for quality, so the image manipulations have the best source to work with
        })

        if (!result.canceled) {
          setSelectedAsset(result.assets[0])

          return result.assets[0]
        }
      } catch (error) {
        // the picker rejects on things like a failed crop, and an unhandled rejection here just looks like a dead button
        console.warn('[media] camera failed', error)
        showMediaError()
      }
    }

    return null
  }, [permissionsDialog, showMediaError])

  const getLibraryMedia = useCallback(async () => {
    // check to see if we already have permission. this doesn't trigger the request dialog, it only checks for
    // existing permissions
    const { status: existingStatus } = await getMediaLibraryPermissionsAsync()

    let finalStatus = existingStatus

    // if they have previously denied access in the past, we can only prompt the user to re-enable permissions in the
    // OS settings, we can't do anything else until they do
    if (existingStatus === 'denied') {
      permissionsDialog.open()
    } else if (existingStatus === 'undetermined') {
      // if we've never asked before, let's do so now!
      const { status } = await requestMediaLibraryPermissionsAsync()

      if (status === 'denied') {
        permissionsDialog.open()
      }

      finalStatus = status
    }

    // either we already had it, or they just allowed it, but we have permissions now
    if (finalStatus === 'granted') {
      setSelectedAsset(null)

      try {
        const result = await launchImageLibraryAsync({
          allowsEditing: true,
          aspect: [4, 4], // 1:1
          exif: false,
          mediaTypes: ['images'],
          quality: 1 // optimize for quality, so the image manipulations have the best source to work with
        })

        if (!result.canceled) {
          setSelectedAsset(result.assets[0])

          return result.assets[0]
        }
      } catch (error) {
        // the picker rejects on things like a failed crop, and an unhandled rejection here just looks like a dead button
        console.warn('[media] library failed', error)
        showMediaError()
      }
    }

    return null
  }, [permissionsDialog, showMediaError])

  return (
    <MediaContext.Provider
      value={{
        isUploadingMedia,
        getCameraMedia,
        getLibraryMedia,
        selectedAsset,
        uploadMedia
      }}
    >
      {children}
      <Dialog
        confirmLabel='Open Settings'
        isOpen={showPermissionsDialog}
        onClose={permissionsDialog.close}
        onConfirm={useCallback(async () => {
          permissionsDialog.close()
          await Linking.openSettings()
        }, [permissionsDialog])}
        title='Media Access Disabled'
      >
        Camera or media library access appears to be disabled on your device for Dateability! You must enable them in
        your phone's settings and then try again.
      </Dialog>
    </MediaContext.Provider>
  )
}
